import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Send, Clock, Facebook, Instagram, Linkedin } from "lucide-react";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [hp, setHp] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  useEffect(() => { api.get("/public/homepage").then((r) => setHp(r.data)).catch(() => {}); }, []);
  const submit = (e) => {
    e.preventDefault();
    toast.success("Thank you! We'll get back to you within 24 hours.");
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const address = hp?.contact_address || "Ram Shyam Niketan Apartment, Nehru Nagar Rd, near Patliputra Police Station, Patna, Bihar 800001";
  const phoneDisplay = hp?.contact_phone || "+91 9155561892";
  const phoneTel = phoneDisplay.replace(/[^0-9+]/g, "");
  const emailAddr = hp?.contact_email || "contact@pmrwelfarefoundation.in";
  const mapQuery = encodeURIComponent(`PMR Welfare Foundation, ${address}`);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <section className="bg-gradient-to-b from-blue-50/60 to-white border-b border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">Get in Touch</div>
          <h1 className="heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black">Let's Talk</h1>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Have a question, want to partner, or need help with a donation? We're here to help.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-[1fr_1.2fr] gap-10">
        {/* Left — Info */}
        <div className="space-y-4">
          <div className="p-6 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#0b1637] text-white shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center shrink-0"><MapPin size={20} className="text-white" /></div>
              <div>
                <div className="text-xs font-black uppercase tracking-[0.2em] text-amber-300">Registered Office</div>
                <div className="mt-2 font-bold leading-relaxed text-white">PMR Welfare Foundation</div>
                <div className="text-sm text-white mt-1 leading-relaxed" data-testid="contact-address">{address}</div>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-slate-200 bg-white flex items-start gap-4 hover:border-brand transition">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-brand flex items-center justify-center shrink-0"><Phone size={18} /></div>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Phone</div>
              <a href={`tel:${phoneTel}`} className="mt-1 font-bold text-lg block text-slate-900 hover:text-brand" data-testid="contact-phone">{phoneDisplay}</a>
              <div className="text-xs text-slate-500">Mon–Sat, 10:00 AM – 7:00 PM IST</div>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-slate-200 bg-white flex items-start gap-4 hover:border-brand transition">
            <div className="w-11 h-11 rounded-xl bg-amber-50 text-[#F59E0B] flex items-center justify-center shrink-0"><Mail size={18} /></div>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Email</div>
              <a href={`mailto:${emailAddr}`} className="mt-1 font-bold text-lg block text-slate-900 hover:text-brand break-all" data-testid="contact-email">{emailAddr}</a>
              <div className="text-xs text-slate-500">Average response: 24 hours</div>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-slate-200 bg-white flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Clock size={18} /></div>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Office Hours</div>
              <div className="mt-1 text-sm">Monday – Saturday: 10:00 AM – 7:00 PM<br />Sunday: Closed</div>
            </div>
          </div>

          <div className="p-4 rounded-2xl border border-slate-200 bg-white">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Follow Us</div>
            <div className="flex gap-2">
              {[Facebook, Instagram, Linkedin].map((I, i) => (
                <a key={i} href="#" className="w-10 h-10 rounded-full bg-slate-100 hover:bg-brand hover:text-white flex items-center justify-center transition"><I size={16} /></a>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Form */}
        <form onSubmit={submit} className="p-6 sm:p-8 rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="contact-form">
          <h2 className="heading text-2xl font-black">Send a Message</h2>
          <p className="text-sm text-slate-500 mt-1">Fill in the form and we'll get back to you soon.</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <Inp label="Your Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} required test="contact-name-input" />
            <Inp label="Email *" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} required test="contact-email-input" />
            <Inp label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} test="contact-phone-input" />
            <Inp label="Subject" v={form.subject} on={(v) => setForm({ ...form, subject: v })} test="contact-subject-input" />
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Message *</label>
              <textarea rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-brand outline-none resize-none" data-testid="contact-message-input" />
            </div>
          </div>
          <button type="submit" className="mt-5 bg-brand hover:bg-[#172b66] text-white font-bold rounded-full px-7 py-3 inline-flex items-center gap-2" data-testid="contact-submit-button">
            <Send size={16} /> Send Message
          </button>
        </form>
      </section>

      {/* Map */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="heading text-2xl font-black">Find Us on the Map</h2>
        <div className="mt-4 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          <iframe
            title="PMR Welfare Foundation Location"
            src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
            width="100%"
            height="360"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0 }}
          />
        </div>
      </section>

      <PublicFooter contact={hp} />
      <WhatsAppButton number={hp?.whatsapp_number || "919155561892"} />
    </div>
  );
}

function Inp({ label, v, on, type = "text", required, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input type={type} required={required} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-brand outline-none" data-testid={test} />
    </div>
  );
}
